/**
 * Phase 6e.4.5 — defensive defaulter for payment.firstPaymentDate.
 *
 * Backend's `buildPaymentVector` uses `firstPaymentDate` as the anchor
 * for `monthIndex` (months from commencement). The combination of
 * (commencementDate, payment.timing, payment.frequency) uniquely
 * determines the canonical first-payment date:
 *
 *   IN_ADVANCE: first payment is AT commencement (monthIndex === 0).
 *     → firstPaymentDate = commencementDate
 *
 *   IN_ARREARS: first payment is one period AFTER commencement.
 *     → firstPaymentDate = commencementDate + monthsPerPeriod[frequency]
 *       e.g. MONTHLY → +1 month, QUARTERLY → +3, SEMI_ANNUAL → +6,
 *            ANNUAL → +12.
 *
 * Edge case — grace periods: the auditor may override the auto-computed
 * value (e.g. a 3-month rent-free start period offsets the first cash
 * payment). The form's dirty-flag tracking respects manual overrides;
 * this helper is only consulted when the field is in its pristine state
 * or when an Excel import row lacks the column.
 *
 * Day-of-month logic: anchor to commencementDate's day-of-month,
 * clamping to the last day of shorter months (e.g. Jan 31 → Feb 28/29).
 * Matches `generatePaymentDates` / `generateHistoricalRateDates`
 * convention and Backend Spec §20.6.
 */

import type { PaymentFrequency, PaymentTiming } from "@/types";

const MONTHS_PER_PERIOD: Record<PaymentFrequency, number> = {
  MONTHLY: 1,
  QUARTERLY: 3,
  SEMI_ANNUAL: 6,
  ANNUAL: 12,
};

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function computeFirstPaymentDate(
  commencementDate: string,
  timing: PaymentTiming,
  frequency: PaymentFrequency,
): string {
  // Defensive: bail out early if `commencementDate` is malformed. The
  // caller handles the empty-string fallback — we never want this
  // helper to throw because it runs inside a `useEffect` and during
  // `preparePayload`.
  if (!commencementDate || !ISO_DATE_RE.test(commencementDate)) {
    return "";
  }

  if (timing === "IN_ADVANCE") {
    return commencementDate;
  }

  const offsetMonths = MONTHS_PER_PERIOD[frequency];
  return addMonthsISO(commencementDate, offsetMonths);
}

function addMonthsISO(iso: string, months: number): string {
  const [yStr, mStr, dStr] = iso.split("-");
  const startY = Number(yStr);
  const startM = Number(mStr) - 1; // 0-indexed
  const anchorDay = Number(dStr);

  const targetTotalMonths = startM + months;
  const targetY = startY + Math.floor(targetTotalMonths / 12);
  const targetM = ((targetTotalMonths % 12) + 12) % 12;

  // Clamp day-of-month: e.g. Jan 31 + 1 mo → Feb 28/29.
  const daysInTargetMonth = new Date(
    Date.UTC(targetY, targetM + 1, 0),
  ).getUTCDate();
  const day = Math.min(anchorDay, daysInTargetMonth);

  return `${String(targetY).padStart(4, "0")}-${String(targetM + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}
